import { withTheme } from '@emotion/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import compose from 'utils/compose';

import { authSelectors } from 'app-components/flux/auth';
import { featureFlagsActions } from 'app-components/flux/feature-flags';
import { tracker } from 'app-components/utils/core';

import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import QTip from 'components/QuickenControls/QTip';
import { withStyles } from 'tss-react/mui';

import { appRefreshAll as refreshAllAction } from 'data/app/actions';
import * as selectors from 'data/app/selectors';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

import { Box } from '@mui/material';
import NavIconRefresh from 'assets/icons/nav-icons/refresh.inline.svg';

const styles = {
  buttonRoot: {
    outline: 'none !important',
    fontSize: 'unset',
  },
  refreshButtonDisabled: {
    opacity: 0.6,
  },
  refreshDivButton: {},
};

const RefreshButton = (props) => {
  const {
    classes,
    disableRefreshButton,
    isRefreshing,
    refreshAll,
    dispatchUpdateChangedFeatureFlags,
    datasetId,
    updateAllFailed,
    theme,
    isInAccountBar,
    updateAllMessages,
    closeHeader,
  } = props;

  const refreshAction = (event) => {
    if (event.altKey && event.shiftKey && event.metaKey) {
      // backdoor to force refresh
      dispatchUpdateChangedFeatureFlags({
        ignoreRefreshThrottle: true,
      });
      dispatchSimpleNotification('force refresh');
    }

    tracker.track(tracker.events.navMenuClick, { menuItem: 'Refresh', section: 'main' });
    closeHeader?.();
    event.stopPropagation();
    refreshAll();
  };

  const size = 20;
  const RefreshIcon = <NavIconRefresh height={size} width={size} stroke={'currentColor'} style={{ margin: 2 }} />;

  // Main icon button that acme and quicken use
  const RefreshIconRoot = (
    <IconButton
      tabIndex={isInAccountBar ? -1 : 0}
      id='refresh-all-icon-button'
      aria-label='Refresh All'
      disabled={disableRefreshButton}
      classes={{
        root: classes.buttonRoot,
        disabled: classes.refreshButtonDisabled,
      }}
      style={{
        color: !isInAccountBar && theme.palette.icon.main,
        padding: !isInAccountBar && 0,
        marginLeft: !isInAccountBar && 4,
        marginRight: !isInAccountBar && 5,
      }}
      onClick={refreshAction}
      size='medium'
      sx={{ height: 23, width: 23 }}
    >
      {updateAllFailed ? <SyncProblemIcon style={{ fontSize: size }} /> : RefreshIcon}
    </IconButton>
  );

  if (!datasetId) {
    return null;
  }

  return isInAccountBar ? (
    <>
      {isRefreshing && (
        <IconButton aria-label='Refresh All Busy' disabled classes={{ root: classes.buttonRoot }} size='medium'>
          <CircularProgress size={size || 22} style={{ color: theme.palette.primary.main }} />
        </IconButton>
      )}
      {!isRefreshing && (
        <QTip
          html={
            updateAllMessages && updateAllMessages.size ? (
              updateAllMessages.map((message) => <div key={message}>{message}</div>)
            ) : (
              <div>Update All</div>
            )
          }
          placement='right'
        >
          {RefreshIconRoot}
        </QTip>
      )}
    </>
  ) : (
    <Box
      role='button'
      tabIndex={0}
      onClick={refreshAction}
      onKeyPress={refreshAction}
      display={'flex'}
      flexDirection={'row'}
      gap={2}
      sx={{
        opacity: disableRefreshButton && theme.palette.action.disabledOpacity,
        alignItems: 'center',
      }}
      className={classNames(classes.refreshDivButton)}
    >
      {isRefreshing ? (
        <IconButton aria-label='Refresh All Busy' disabled classes={{ root: classes.buttonRoot }} size='small'>
          <CircularProgress size={15} color='primary' style={{ marginLeft: 3, marginRight: 4 }} />
        </IconButton>
      ) : (
        <QTip html={updateAllMessages?.map?.((message) => <div key={message}>{message}</div>)} placement='right'>
          {RefreshIconRoot}
        </QTip>
      )}

      <Typography variant='paragraphRegular' color='textPrimary'>
        {isRefreshing ? 'Refreshing...' : 'Refresh'}
      </Typography>
    </Box>
  );
};

RefreshButton.propTypes = {
  disableRefreshButton: PropTypes.bool,
  isRefreshing: PropTypes.bool,
  updateAllFailed: PropTypes.bool,
  refreshAll: PropTypes.func,
  dispatchUpdateChangedFeatureFlags: PropTypes.func,
  classes: PropTypes.object,
  theme: PropTypes.object,
  updateAllMessages: PropTypes.object,
  datasetId: PropTypes.string,
  isInAccountBar: PropTypes.bool,
  closeHeader: PropTypes.func,
};

const mapStateToProps = (state) => ({
  disableRefreshButton: selectors.disableRefreshButton(state),
  isRefreshing: selectors.isRefreshing(state),
  updateAllFailed: selectors.getUpdateAllFailed(state),
  updateAllMessages: selectors.getUpdateAllMessages(state),
  datasetId: authSelectors.getDatasetId(state),
});

const mapDispatchToProps = (dispatch) => ({
  refreshAll: () => dispatch(refreshAllAction(undefined, { context: 'refreshAll' })),
  dispatchUpdateChangedFeatureFlags: (flags) => dispatch(featureFlagsActions.applyFeatureFlagsChanges(flags)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTheme,
)(withStyles(RefreshButton, styles, { name: 'RefreshButton' }));
